/**
 * Resource deep-dive drawer — top resources for one service in one week.
 *
 * A v1 substitute for cost-allocation tags (AWS tagging is currently absent),
 * hence the "by tag — coming soon" affordance. Purely presentational: the tab
 * owns the query so the same drawer serves AWS and GCP.
 */

import { useTranslation } from 'react-i18next';
import { Tag } from 'lucide-react';
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from '@/components/ui/sheet';
import type { FinopsResourceRow } from '../../../../shared/types';
import { EmptyState, LoaderBlock, fmtUSDCents } from './shared';

export function ResourceDrawer({
  open,
  onOpenChange,
  service,
  periodLabel,
  rows,
  isLoading,
  entityColLabel,
  emptyDescription,
  filterLabel,
  filterNote,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  service?: string;
  periodLabel?: string;
  rows: FinopsResourceRow[];
  isLoading?: boolean;
  entityColLabel: string;
  emptyDescription?: string;
  /** Active account/project filter, appended to the subtitle. */
  filterLabel?: string;
  /** Caveat about what the filter excludes — the caller decides, the drawer never infers it. */
  filterNote?: string;
}) {
  const { t } = useTranslation();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>{service ?? t('finops.cloud.resources_title')}</SheetTitle>
          <SheetDescription>
            {t('finops.cloud.resources_subtitle', { period: periodLabel ?? '—' })}
            {filterLabel ? ` · ${filterLabel}` : ''}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filterNote && (
            <p className="mb-3 text-xs text-muted-foreground">{filterNote}</p>
          )}
          {isLoading ? (
            <LoaderBlock />
          ) : rows.length === 0 ? (
            <EmptyState
              title={t('finops.cloud.resources_empty_title')}
              description={emptyDescription ?? t('finops.cloud.resources_empty_description')}
            />
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground border-b">
                <tr>
                  <th className="py-2 pr-3">{t('finops.cloud.col_resource')}</th>
                  <th className="pr-3">{entityColLabel}</th>
                  <th className="text-right">{t('finops.cloud.col_cost')}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.accountId}-${r.resourceId}`} className="border-b last:border-0">
                    <td className="py-2 pr-3">
                      {r.isOther ? (
                        <span className="text-muted-foreground italic">{t('finops.cloud.resources_other')}</span>
                      ) : (
                        <span className="font-mono text-xs break-all">{r.resourceId}</span>
                      )}
                    </td>
                    <td className="pr-3 text-xs text-muted-foreground">{r.accountName || r.accountId}</td>
                    <td className="text-right tabular-nums whitespace-nowrap">{fmtUSDCents(r.cost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="border-t px-6 py-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Tag className="h-3.5 w-3.5 shrink-0" />
          {t('finops.cloud.resources_by_tag_soon')}
        </div>
      </SheetContent>
    </Sheet>
  );
}
